/**
 * Personal mutes: "I don't want to hear this player", on this device, told to
 * nobody (docs/VOICE_CHAT.md, "Muting").
 *
 * A mute is kept twice. By PLAYER ID, permanently: that is the person. And by
 * NICKNAME, but only within the lobby the mute was made in: an anonymous guest
 * who rejoins from a fresh browser profile arrives with a new uid, and a mute
 * that forgot them because of it would fail against exactly the people it is
 * for. The nickname half is scoped because nicknames are not unique - matched
 * forever, it would silently mute every future "Alex" at every table.
 *
 * Nicknames compare case-insensitively, as the server's own nickname checks do
 * (strings.EqualFold), so the rejoin cannot dodge it by changing case.
 */
import { browserStorage, readJson, type KeyValueStorage } from "./storage";

const MUTES_KEY = "fiveout:voice.mutes";

/**
 * Bounds, so a device that plays for years does not grow the list forever. The
 * id list drops its oldest entry first; nickname entries also lapse after a
 * couple of days, by which time the lobby they were scoped to is long gone.
 */
const MAX_IDS = 500;
const MAX_NICKNAMES = 200;
const NICKNAME_TTL_MS = 2 * 24 * 60 * 60 * 1000;

export interface NicknameMute {
  lobbyId: string;
  /** Normalised: trimmed and lower-cased. */
  nickname: string;
  atMs: number;
}

export interface MuteList {
  ids: string[];
  nicknames: NicknameMute[];
}

const normalize = (nickname: string) => nickname.trim().toLowerCase();

export function loadMutes(storage: KeyValueStorage = browserStorage(), now = Date.now()): MuteList {
  const raw = readJson(storage, MUTES_KEY);
  const list: MuteList = { ids: [], nicknames: [] };
  if (!raw || typeof raw !== "object") return list;
  const { ids, nicknames } = raw as { ids?: unknown; nicknames?: unknown };
  if (Array.isArray(ids)) list.ids = ids.filter((id): id is string => typeof id === "string" && id !== "");
  if (Array.isArray(nicknames)) {
    list.nicknames = nicknames.filter(
      (n): n is NicknameMute =>
        !!n &&
        typeof n === "object" &&
        typeof (n as NicknameMute).lobbyId === "string" &&
        typeof (n as NicknameMute).nickname === "string" &&
        typeof (n as NicknameMute).atMs === "number" &&
        now - (n as NicknameMute).atMs < NICKNAME_TTL_MS,
    );
  }
  return list;
}

function saveMutes(list: MuteList, storage: KeyValueStorage): void {
  if (list.ids.length === 0 && list.nicknames.length === 0) storage.removeItem(MUTES_KEY);
  else storage.setItem(MUTES_KEY, JSON.stringify(list));
}

/** The check against a list already loaded, for a reconcile that asks about every member. */
export function isMutedIn(list: MuteList, playerId: string, nickname: string | undefined, lobbyId: string): boolean {
  if (list.ids.includes(playerId)) return true;
  if (!nickname) return false;
  const name = normalize(nickname);
  return name !== "" && list.nicknames.some((n) => n.lobbyId === lobbyId && n.nickname === name);
}

export function isMuted(
  playerId: string,
  nickname: string | undefined,
  lobbyId: string,
  storage: KeyValueStorage = browserStorage(),
  now = Date.now(),
): boolean {
  return isMutedIn(loadMutes(storage, now), playerId, nickname, lobbyId);
}

export function mute(
  playerId: string,
  nickname: string | undefined,
  lobbyId: string,
  storage: KeyValueStorage = browserStorage(),
  now = Date.now(),
): void {
  const list = loadMutes(storage, now);
  list.ids = [...list.ids.filter((id) => id !== playerId), playerId].slice(-MAX_IDS);
  const name = nickname ? normalize(nickname) : "";
  if (name) {
    list.nicknames = [
      ...list.nicknames.filter((n) => !(n.lobbyId === lobbyId && n.nickname === name)),
      { lobbyId, nickname: name, atMs: now },
    ].slice(-MAX_NICKNAMES);
  }
  saveMutes(list, storage);
}

/**
 * Lifts both halves. Leaving the nickname behind would make Unmute look broken:
 * the row's id is unmuted, and the player stays silent because their name
 * still matches.
 */
export function unmute(
  playerId: string,
  nickname: string | undefined,
  lobbyId: string,
  storage: KeyValueStorage = browserStorage(),
  now = Date.now(),
): void {
  const list = loadMutes(storage, now);
  list.ids = list.ids.filter((id) => id !== playerId);
  const name = nickname ? normalize(nickname) : "";
  if (name) list.nicknames = list.nicknames.filter((n) => !(n.lobbyId === lobbyId && n.nickname === name));
  saveMutes(list, storage);
}
